import PropTypes from "prop-types";
import React from "react";

import { Text, textPropTypes } from "@swa-ui/core";
import i18n from "@swa-ui/locale";
import { classNames } from "@swa-ui/string";

import styles from "./FlightNumbers.module.scss";

/**
 * FlightNumbers displays a list of flight numbers optional with a separator character.
 */
export const FlightNumbers = (props) => {
  const { className, flightNumbers, separator = "", showHashtag = true, textProps } = props;

  return (
    <div {...getProps()}>
      {renderHashTag()}
      <ul className={styles.listContainer}>{flightNumbers.map(renderFlightNumber)}</ul>
    </div>
  );

  function renderHashTag() {
    return showHashtag ? (
      <Text element="span" {...textProps}>
        #
      </Text>
    ) : null;
  }

  function renderFlightNumber(flightInfo, index) {
    const { airlineCode, flightNumber } = flightInfo;

    return (
      <Text element="li" key={flightNumber} {...textProps}>
        {airlineCode}
        {flightNumber}
        {index < flightNumbers.length - 1 ? renderSeparator() : null}
      </Text>
    );
  }

  function renderSeparator() {
    return separator ? <>&nbsp;{separator}&nbsp;</> : <>&nbsp;</>;
  }

  function getProps() {
    return {
      ["aria-label"]: getFlightNumberAriaLabel(),
      className: classNames(className, styles.flightNumbers),
    };
  }

  function getFlightNumberAriaLabel() {
    return i18n("FlightNumbers__FLIGHT_INFORMATION_ARIA_LABEL", {
      flightCount: flightNumbers.length,
      flightNumberContent: getFlightNumberContent(),
    });
  }

  function getFlightNumberContent() {
    return flightNumbers
      .map((flightNumberInfo) => {
        const { airlineCode, flightNumber } = flightNumberInfo;

        return `${airlineCode ? `${airlineCode} ` : ""}${flightNumber}`;
      })
      .join(",");
  }
};

FlightNumbers.propTypes = {
  /** Additional class to be added to the outermost element for positioning the component. */
  className: PropTypes.string,

  /** Array of flight numbers with optional airline codes. */
  flightNumbers: PropTypes.arrayOf(
    PropTypes.shape(
      [
        {
          /** Airline code that will be rendered. */
          airlineCode: PropTypes.string,

          /** Content that will be rendered inside the component. */
          flightNumber: PropTypes.string.isRequired,
        },
      ].isRequired
    )
  ),

  /** Content to be rendered between each flight number. */
  separator: PropTypes.node,

  /** Boolean value to show or hide the hashtag symbol. */
  showHashtag: PropTypes.bool,

  /** Props passed to the underlying Text component. See Text for more details. */
  textProps: PropTypes.shape(textPropTypes),
};
